import { type ReactNode } from "react";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";
import { useTranslation } from "@i18n";
import type { TranslationKey } from "@i18n";

interface GridPageHeaderLeftProps {
  /** Icon name passed to IconComponent. Omit for settings/portal grids that don't use icons. */
  icon?: string | undefined;
  /** Page title — a TranslationKey string (auto-wrapped in t()) or a ReactNode for custom content */
  title: TranslationKey | ReactNode;
  /** Optional description — a TranslationKey string (auto-wrapped in t()) or a ReactNode for custom content */
  description?: TranslationKey | ReactNode;
}

interface GridPageHeaderProps {
  /** Icon name passed to IconComponent. Omit for settings/portal grids. */
  icon?: string | undefined;
  /** Page title — a TranslationKey string (auto-wrapped in t()) or a ReactNode for custom content */
  title: TranslationKey | ReactNode;
  /** Optional description — a TranslationKey string (auto-wrapped in t()) or a ReactNode for custom content */
  description?: TranslationKey | ReactNode;
  /** Custom rendered template instead of the default header. Use GridPageHeaderLeft inside for the standard left-side content. */
  customHeader?: ReactNode;
}

/*
 * Every optional member below carries its `| undefined` arm. This layout is mounted from ~40 module
 * pages that forward their OWN optional state straight through (`icon={module?.icon}`,
 * `className={props.className}`), so an omitted prop arrives here as an explicit `undefined`. Each
 * one is destructured with a default just below, i.e. an absent key and an `undefined` one land on
 * exactly the same value; nothing here counts keys or uses `in`.
 */
interface GridPageLayoutProps extends Partial<GridPageHeaderProps> {
  /** Whether to show the title/header section. Defaults to true. */
  showHeader?: boolean | undefined;
  /** Main grid content (SlickGrid1 + optional sidebar) */
  children: ReactNode;
  /** Additional className on the outermost gray wrapper */
  outerClassName?: string | undefined;
  /** Additional className on the inner white card */
  className?: string | undefined;
  /** Additional className on the grid container div */
  gridContainerClassName?: string | undefined;
  allowOuterPadding?: boolean | undefined;
}

/**
 * Reusable left-side header block: optional icon + title + description. Exported so consumers can compose
 * custom headers while keeping the standard left side. Module grids (Issues, Files, Reviews) pass an
 * icon; settings/portal grids do not.
 */
function GridPageHeaderLeft({ icon, title, description }: GridPageHeaderLeftProps) {
  const { t, i18n } = useTranslation();
  const isStringTitle = typeof title === "string";
  const isStringDesc = typeof description === "string";

  // `title`/`description` are documented as "a TranslationKey, or a ReactNode for custom content",
  // but `TranslationKey | ReactNode` collapses to plain `ReactNode`: a string is indistinguishable
  // from a key at compile time, and callers legitimately pass runtime text (Forms passes the module
  // name). Ask the resource store which one it is rather than asserting - i18next echoes an unknown
  // key back verbatim, so the non-key branches below render exactly what `t()` was already
  // returning for them.
  const isTranslationKey = (value: string): value is TranslationKey => i18n.exists(value);

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-start gap-x-2">
        {icon && <IconComponent name={icon} width="1.5rem" height="1.5rem" />}
        {isStringTitle ? (
          <Text className="text-2xl font-semibold">{isTranslationKey(title) ? t(title) : title}</Text>
        ) : (
          <div className="text-2xl font-semibold">{title}</div>
        )}
      </div>
      {description &&
        (isStringDesc ? (
          <Text className="text-sm">{isTranslationKey(description) ? t(description) : description}</Text>
        ) : (
          <div className="text-sm">{description}</div>
        ))}
    </div>
  );
}

function GridPageHeader({ icon, title, description, customHeader }: GridPageHeaderProps) {
  if (customHeader) {
    return <>{customHeader}</>;
  }

  return (
    <div className="flex flex-col pb-4">
      <GridPageHeaderLeft icon={icon} title={title} description={description} />
    </div>
  );
}

/**
 * Standardised grid page layout used across the platform.
 *
 * Structure:
 *   outer gray shell (bg-[#F0F5F9] rounded-lg)
 *     └─ inner white card (border rounded-lg p-4 bg-white)
 *          ├─ header (icon + title + description, or customHeader)
 *          └─ grid container (children)
 */
function GridPageLayout({
  showHeader = true,
  icon,
  title,
  description,
  customHeader,
  children,
  outerClassName = "",
  className = "",
  gridContainerClassName = "",
  allowOuterPadding = true,
}: GridPageLayoutProps) {
  return (
    <div
      className={`w-full h-full bg-[#F0F5F9] ${allowOuterPadding ? "pe-4" : ""} rounded-lg overflow-hidden ${outerClassName}`.trim()}
    >
      <div
        className={`flex flex-col border h-full w-full overflow-auto border-stroke_primary_light rounded-lg p-4 bg-white ${className}`.trim()}
      >
        {showHeader && (customHeader || title) && (
          <GridPageHeader icon={icon} title={title ?? ""} description={description} customHeader={customHeader} />
        )}
        <div className={`flex w-full overflow-auto h-full ${gridContainerClassName}`.trim()}>
          <div className="h-full flex gap-4 w-full">{children}</div>
        </div>
      </div>
    </div>
  );
}

export { GridPageLayout, GridPageHeader, GridPageHeaderLeft };
export type { GridPageLayoutProps, GridPageHeaderProps, GridPageHeaderLeftProps };
export default GridPageLayout;
